import type { FirstWeekStepKey } from 'aeo-schema-generator-shared/types'
import { FIRST_WEEK_STEP_TITLES } from 'aeo-schema-generator-shared/utils'

/*
  Every string the first-week guide shows. Written the way a person says it.
  No em dashes. No invented numbers: effort is in words, never minutes.
*/

export const FIRST_WEEK_TITLE = 'Your first week'

/*
  The deal for an account with no plan: what is free, and where a plan
  begins. It promises the free page, so it ends on the paid HubSpot plan
  that page needs, as the free page step's own line below does.
*/
export const MANUAL_FREE_LINE = 'Your first page is free. Copy the schema and install it on your website. A plan with us starts when you want the next one or want this one watched.'

export const FREE_LINE = 'Your first page is free. It needs a paid HubSpot plan. A plan with us starts when you want the next one or want this one watched.'

/*
  The day the money moves, written so it cannot break across two lines. This
  sentence is the one a person checks before trusting us with a card, and a
  date split between "September" and "9" is exactly where they stop reading.
*/
function chargeDay(endsAt: string): string {
  const date = new Date(endsAt).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })
  // Hold the month and the day together; the weekday may still wrap.
  return date.replace(/(\w+) (\d+)$/, '$1\u00a0$2')
}

export function trialLine(planName: string | null, endsAt: string | null): string {
  const plan = planName ? `${planName} trial` : 'trial'
  if (!endsAt) return `Your ${plan} is on. Nothing is charged until it ends.`
  return `Your ${plan} runs until ${chargeDay(endsAt)}. Nothing is charged before then.`
}

/*
  The last-two-days sentence, in three parts so the panel can make the word
  "Billing" a link without slicing the sentence apart by string.
*/
export function endingSoonParts(endsAt: string): { before: string; linkWord: string; after: string } {
  return { before: `Your trial ends ${chargeDay(endsAt)}. You can cancel any time before then in `, linkWord: 'Billing', after: '.' }
}

export const COLLAPSED_LINE = (done: number, total: number) => `Setup: ${done} of ${total} done`

export interface StepCopy {
  title: string
  line: string
  where: string
  effort: string
  action: string
  doneLabel: string
}

export const STEP_COPY: Record<FirstWeekStepKey, StepCopy> = {
  add_site: {
    title: FIRST_WEEK_STEP_TITLES.add_site,
    line: 'Everything below is about one site. Start with the one we scanned.',
    where: 'Your site appears as a card below.',
    effort: 'One field, filled in for you',
    action: 'Add your site',
    doneLabel: 'Added',
  },
  report: {
    title: FIRST_WEEK_STEP_TITLES.report,
    line: 'Your page beside what AI is told about it, detail by detail.',
    where: 'Opens as a page of its own, with the fix for your page on it.',
    effort: 'A short wait while we read your site',
    action: 'See your report',
    doneLabel: 'Seen',
  },
  profile: {
    title: FIRST_WEEK_STEP_TITLES.profile,
    line: 'Your name, what you do, and where. This is what every page we write says about you.',
    where: 'On every page we write, as your name, what you do and where.',
    effort: 'A few fields, filled in for you',
    action: 'Confirm your profile',
    doneLabel: 'Confirmed',
  },
  hubspot: {
    title: FIRST_WEEK_STEP_TITLES.hubspot,
    line: 'So we can put schema on your pages for you, and check it landed.',
    where: 'Your HubSpot pages appear under Your pages.',
    effort: 'One sign-in',
    action: 'Connect HubSpot',
    doneLabel: 'Connected',
  },
  invite: {
    title: FIRST_WEEK_STEP_TITLES.invite,
    line: 'They will put two things on the site: the pixel and your first page. You will see both land here.',
    where: 'Their work shows up on this screen as it happens.',
    effort: 'One email address',
    action: 'Invite them',
    doneLabel: 'Joined',
  },
  free_page: {
    title: FIRST_WEEK_STEP_TITLES.free_page,
    line: 'See what we wrote for your page, then send it to HubSpot in one click. We read the page back to confirm it. Free, with no card. It needs a paid HubSpot plan.',
    where: 'Your page says it is live once we have confirmed it.',
    effort: 'One click',
    action: 'See your page',
    doneLabel: 'Live',
  },
  first_page: {
    title: FIRST_WEEK_STEP_TITLES.first_page,
    line: 'We write the schema for one page, starting with your home page.',
    where: 'Counts under Pages set up, top of this screen.',
    effort: 'One click',
    action: 'Set up a page',
    doneLabel: 'Written',
  },
  live: {
    title: FIRST_WEEK_STEP_TITLES.live,
    line: 'We check the page from outside and tell you what AI can now read.',
    where: 'The page card turns Confirmed and you get an email.',
    effort: 'One click if you use HubSpot. A copy and paste otherwise.',
    action: 'Put it on the page',
    doneLabel: 'Live',
  },
  pixel: {
    title: FIRST_WEEK_STEP_TITLES.pixel,
    line: 'One line on your site tells us when an AI bot reads a page.',
    where: 'Bot visits appear under Who has been reading your site. The first one arrives by email.',
    effort: 'A copy and paste',
    action: 'Get the pixel',
    doneLabel: 'Receiving',
  },
  watch: {
    title: FIRST_WEEK_STEP_TITLES.watch,
    line: 'We check it every night and only tell you if something changes.',
    where: 'Counts under Pages watched.',
    effort: 'One click',
    action: 'Watch this page',
    doneLabel: 'Watching',
  },
}

/*
  What changes for an account with no plan. Watching comes with a plan, so
  the step says so before the button is pressed instead of after it. The
  other rows drop what is only true on a trial: a site already scanned, a
  pixel to install, schema on more than one page.

  How often a plan checks is said as the least any plan does: the entry
  plan checks every week. The page detail's plan card says the same ("We
  check your pages every week"), and a test holds the two together.
*/
/* What the free guide asks of the person invited. Said on the invite step and again in the invite itself. */
const FREE_INVITE_LINE = 'They will connect HubSpot and put your first page live. You will see it land here.'

const FREE_STEP_COPY: Partial<Record<FirstWeekStepKey, Partial<StepCopy>>> = {
  add_site: {
    line: 'Everything below is about one site. Tell us which one.',
    effort: 'One field',
  },
  hubspot: {
    line: 'So we can put the fix on your page for you, and check it landed.',
  },
  invite: {
    line: FREE_INVITE_LINE,
  },
  watch: {
    line: 'With a plan, we check this page every week and only tell you if something changes.',
    effort: 'Comes with a plan',
    action: 'See plans',
  },
}

/** A step's copy for the guide it is drawn in: the trial's, or the free account's. */
export function stepCopy(key: FirstWeekStepKey, variant: 'trial' | 'free', installationMethod: 'hubspot' | 'manual' = 'hubspot'): StepCopy {
  if (installationMethod === 'manual' && (key === 'free_page' || key === 'live')) {
    return { ...STEP_COPY[key], title: 'Add schema to your website', line: `Copy the code and add it to this page, or share it with the person who manages your website.${variant === 'free' ? ' Your first page is free, with no card.' : ''}`, where: 'Then check the installation. This step is complete only when we find the schema on your page.', effort: 'Copy, install, and check', action: 'Get your code' }
  }
  return variant === 'free' ? { ...STEP_COPY[key], ...FREE_STEP_COPY[key] } : STEP_COPY[key]
}

/*
  The step that ends with a page confirmed live. A trial calls it `live` and
  the free guide calls it `free_page`. The server names a running check
  'live' in both guides, so whatever matches that check to a row, or asks
  whether "the live step" is done, asks here which row that is.
*/
export function liveStepKey(variant: 'trial' | 'free'): 'live' | 'free_page' {
  return variant === 'free' ? 'free_page' : 'live'
}

export const DO_THIS_NOW = 'Do this now'
export const NOT_SEEN_YET = 'Not seen on the page yet.'
export const CHECK_AGAIN = 'Check again'
export const IN_FLIGHT = 'Checking your page now. This takes about a minute.'
export const WAITING_ON_INVITE = (at: string) => `Invited ${new Date(at).toLocaleDateString('en-US', { weekday: 'long' })}. Waiting for them to join.`
export const SEND_AGAIN = 'Send it again'
export const DO_IT_MYSELF = 'I will do it myself'

export const BRANCH_QUESTION = 'Who makes changes to your site?'
export const BRANCH_OPTIONS: { value: 'self' | 'team' | 'agency'; label: string }[] = [
  { value: 'self', label: 'Me' },
  { value: 'team', label: 'Someone on my team' },
  { value: 'agency', label: 'An agency we work with' },
]

export const INVITE_TITLE = 'Invite the person who runs your site'
export const INVITE_LEAD = 'They will put two things on the site, the pixel and your first page. You will see both land here.'
/** The invite's lead for the guide it is opened from. The free guide has no pixel step, so its invite promises none. */
export function inviteLead(variant: 'trial' | 'free'): string {
  return variant === 'free' ? FREE_INVITE_LINE : INVITE_LEAD
}
export const INVITE_COPY_LINK = 'Copy a link'
export const INVITE_LINK_READY = 'Your invite link is ready. Send it however you like.'
export const INVITE_SEAT_FULL = (planName: string | null) => `Your ${planName ?? 'current'} plan has no open seats. Free one up, or move to a bigger plan.`

export const MOMENT_TITLE = (url: string) => `${url.replace(/^https?:\/\//, '')} is live.`
export const MOMENT_LEAD = 'AI can now read this:'
export const MOMENT_LINK = 'See the page'

export const DISMISS = 'Hide this'
export const GUIDE_FAILED = 'We could not load your first-week steps just now. Everything else on this page still works.'

export function evidenceLine(at: string, byName?: string | null, label = 'Confirmed'): string {
  const d = new Date(at)
  const t = d.getTime()
  // 0 is the server's sentinel for "done, but no moment to name" (the
  // HubSpot step in firstWeekSteps.ts); an unparseable date gets the same
  // treatment rather than an "Invalid Date" in the customer's face.
  if (!Number.isFinite(t) || t === 0) return label
  const day = d.toLocaleDateString('en-US', { weekday: 'long' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase()
  return byName ? `${byName} did this ${day} at ${time}` : `${label} ${day} at ${time}`
}

/* ---------------- What a step says while it is working ---------------- */

/*
  Some steps call the server before anything visible happens. Silence after a
  click reads as a broken button, so each of these says what is under way.
*/
export const BUSY_LINES: Partial<Record<FirstWeekStepKey, string>> = {
  watch: 'Turning watching on.',
  first_page: 'Opening your page setup.',
  live: 'Checking your page now.',
  profile: 'Opening your profile.',
  /* The free guide's page: we ask which page is free and find it before it opens. */
  free_page: 'Opening your page.',
}

/* ---------------- What a step says when it cannot finish ---------------- */

export const TRY_AGAIN = 'Try again in a minute.'

export const WATCH_FAILED = `We could not turn watching on just now. ${TRY_AGAIN}`
export const PAGE_UNREACHABLE = `We could not reach your page just now. ${TRY_AGAIN}`
export const SETUP_UNAVAILABLE = `We could not open that page for setup just now. ${TRY_AGAIN}`
export const SITE_LIMIT_REACHED = 'Your plan has all its sites in use. Choose a bigger plan to add another.'
export const SETUP_NEEDS_ADMIN = 'Only an owner or admin on this team can set up pages. Ask them, or ask to be made an admin.'
export const SETUP_NO_SITE = 'Add your site first, then we can set up a page on it.'
export const SETUP_DOMAIN_MISSING = 'That page is not on a site in your workspace yet. Add the site first.'
export const SETUP_AMBIGUOUS = 'More than one page matched. Open your site and pick the page you mean.'

/*
  Asked when somebody jumps to a page before confirming the profile. It is a
  question, not a wall: the profile makes the schema better, and they may
  still have a reason to go on. Both ways out are offered.
*/
export const PROFILE_FIRST_TITLE = 'Confirm your profile first'
export const PROFILE_FIRST_LEAD =
  'Without it we guess your business details from the page. With it, every page we write uses your own words for who you are, what you do, and where you work.'
export const PROFILE_FIRST_GO = 'Confirm your profile'
export const PROFILE_FIRST_SKIP = 'Set up the page anyway'
export const PROFILE_LOOKUP_FAILED = `We could not open your profile just now. ${TRY_AGAIN}`
export const PROFILE_SAVE_FAILED = 'We could not save your profile just now. Nothing was lost; try again in a minute.'

/*
  The watch cap, in three parts so the panel can make "Monitoring" a link
  without slicing the sentence apart by string.
*/
export const WATCH_CAP_BEFORE = (cap: number | undefined) =>
  `Your plan watches ${cap ?? 'a set number of'} pages and all of them are taken. Swap one out under `
export const WATCH_CAP_LINK = 'Monitoring'
export const WATCH_CAP_AFTER = ', or choose a bigger plan.'

/*
  A teammate pressed a step that begins on the start page, and their team has
  no site yet. Only the owner enters the site, so the step waits for them.
*/
export const TEAMMATE_WAITS_FOR_SITE = "Your team's owner adds the site first. This step opens once they have."

/* ---------------- The invite dialog ---------------- */

export const INVITE_CLOSE = 'Close'
export const INVITE_FAILED = `We could not make an invite link just now. ${TRY_AGAIN}`
/* A team with no plan cannot invite yet. Trying again would not change that, so this says what would. */
export const INVITE_NEEDS_PLAN = 'Inviting someone comes with a plan.'
export const INVITE_SEE_PLANS = 'See plans'
export const INVITE_COPY_FAILED = 'Copying did not work here. Here is your link.'
export const INVITE_LINK_LABEL = 'Your invite link'
export const INVITE_SEE_INVITATIONS = 'See invitations'
export const INVITE_BIGGER_PLAN = 'Choose a bigger plan'
